/**
 * Tracker de progresso de processamento de propriedade via SSE.
 *
 * Substitui o polling em `GET /api/v1/process/{file_id}` (descontinuado
 * apos refactor de tracking 2026-05-23 / F-PLAT-REDIS-09 — endpoint le
 * chave Redis legada `consolidate:track:process:{file_id}` que nao e
 * mais escrita; novos sinks: `gn:track:file:{file_id}` HASH + control.*
 * em PostgreSQL).
 *
 * Consome `/events/status/{file_id}` no consolidate-service (mesmo SSE
 * usado por `usePropertyProcessingStream` em PropertyTabs) e acumula
 * eventos `domain_<token>_<phase>` localmente para reconstruir a shape
 * `processStatus` que o overlay (`ProcessingOverlay.jsx`) e os call-sites
 * (`useMapClick`, `UnifiedSearchBar`) esperam:
 *
 *   {
 *     file_id, request_id,
 *     pending_count,                // started − resolved
 *     domains_processed_count,
 *     domains_processed: string[],  // "domain_<token>_started - <code>"
 *     final_status, last_status,
 *     can_retry,
 *   }
 *
 * Ref: D-SL-02 (SSE pelo consolidate como fonte canonica de progresso).
 */

import { openStatusStream } from './ConsolidateService';

const TERMINAL_FINAL_STATUSES = new Set([
  'process_succeeded',
  'process_failed',
  'process_timeout',
  'process_canceled',
  'cache_hit',
]);

const FAILED_FINAL_STATUSES = new Set(['process_failed', 'process_timeout', 'process_canceled']);

const DOMAIN_EVENT_REGEX = /^domain_(.+)_(started|succeeded|failed|skipped)$/;

function classifyStatus(rawStatus) {
  const status = String(rawStatus || '').toLowerCase();
  if (!status) return null;
  if (status === 'cache_hit' || status.startsWith('process_')) {
    return {
      kind: 'process',
      terminal: TERMINAL_FINAL_STATUSES.has(status),
      failed: FAILED_FINAL_STATUSES.has(status),
    };
  }
  const m = status.match(DOMAIN_EVENT_REGEX);
  if (m) return { kind: 'domain', token: m[1], phase: m[2] };
  return null;
}

/**
 * Abre SSE e acumula snapshots `processStatus` ate status terminal.
 *
 * @param {string} fileId
 * @param {object} opts
 * @param {string|null} [opts.requestId]  request_id inicial (ate o BE
 *   propagar via SSE), exibido no overlay (`req: ...`).
 * @param {(snapshot: object) => void} [opts.onSnapshot]  chamado a cada
 *   evento SSE com a snapshot reconstruida.
 * @param {(info: {finalStatus: string, failed: boolean, snapshot: object}) => void} [opts.onTerminal]
 *   chamado uma unica vez quando status terminal e atingido. Caller deve
 *   chamar `cancel()` apos terminar (ex.: depois de hidratar /state).
 * @returns {() => void} cancela a conexao SSE.
 */
export function trackPropertyProgress(fileId, opts = {}) {
  const { requestId = null, onSnapshot, onTerminal } = opts;
  if (!fileId) return () => {};

  /** keys "<token>|<code>" que ja receberam evento `started`. */
  const startedKeys = new Set();
  /** keys "<token>|<code>" resolvidas (succeeded|failed|skipped). */
  const resolvedKeys = new Set();
  /** lista ordenada de entradas para `domains_processed`. */
  const orderedEntries = [];

  let lastStatus = null;
  let finalStatus = null;
  let currentRequestId = requestId || null;
  let terminalFired = false;

  function buildSnapshot() {
    const pending = Math.max(0, startedKeys.size - resolvedKeys.size);
    return {
      file_id: fileId,
      request_id: currentRequestId,
      pending_count: finalStatus ? 0 : pending,
      domains_processed_count: orderedEntries.length,
      domains_processed: orderedEntries.slice(),
      final_status: finalStatus,
      last_status: lastStatus,
      can_retry: Boolean(finalStatus && FAILED_FINAL_STATUSES.has(finalStatus)),
    };
  }

  function emit() {
    if (typeof onSnapshot !== 'function') return;
    try {
      onSnapshot(buildSnapshot());
    } catch (_e) {
      // no-op
    }
  }

  function handlePayload(payload) {
    const status = payload?.status;
    const code = payload?.code;
    const reqId = payload?.request_id;
    if (reqId && !currentRequestId) currentRequestId = String(reqId);
    if (status) lastStatus = String(status);

    const cls = classifyStatus(status);
    if (cls && cls.kind === 'domain' && code) {
      const key = `${cls.token}|${code}`;
      if (cls.phase === 'started') {
        if (!startedKeys.has(key)) {
          startedKeys.add(key);
          orderedEntries.push(`domain_${cls.token}_started - ${code}`);
        }
      } else {
        // succeeded | failed | skipped — eventos terminais podem chegar
        // sem o `started` correspondente (race ou cache-hit no worker).
        if (!startedKeys.has(key)) {
          startedKeys.add(key);
          orderedEntries.push(`domain_${cls.token}_${cls.phase} - ${code}`);
        }
        resolvedKeys.add(key);
      }
    }

    if (cls && cls.kind === 'process' && cls.terminal) {
      finalStatus = String(status).toLowerCase();
    }

    emit();

    if (
      cls &&
      cls.kind === 'process' &&
      cls.terminal &&
      !terminalFired &&
      typeof onTerminal === 'function'
    ) {
      terminalFired = true;
      try {
        onTerminal({
          finalStatus,
          failed: Boolean(cls.failed),
          snapshot: buildSnapshot(),
        });
      } catch (_e) {
        // no-op
      }
    }
  }

  // Emite snapshot inicial (vazio) para o overlay sair do estado "..."
  emit();

  const close = openStatusStream(fileId, {
    onMessage: handlePayload,
    onError: () => {
      // EventSource reconecta automaticamente. Erros transitorios nao
      // sao fatais para o tracker.
    },
  });

  return function cancel() {
    try {
      if (typeof close === 'function') close();
    } catch (_e) {
      // no-op
    }
  };
}

export default trackPropertyProgress;
